/* eslint-disable max-lines-per-function */
import React from "react";
import { bindHover, bindMenu, bindTrigger } from "material-ui-popup-state";
import { Delete, Edit, Home, MailOutline, MoreVert } from "@mui/icons-material";
import { Menu, MenuItem, IconButton, Tooltip } from "@mui/material";
import HoverMenu from "material-ui-popup-state/HoverMenu";
import { usePopupState } from "material-ui-popup-state/hooks";
import { useMutation } from "@apollo/client";
import { useWindowSize } from "pages/eligibility/components/form/form-layout";

import {
  UserProfile_userProfile_addresses as Address,
  DeleteUserProfileAddress,
  SetAsUserProfileCurrentAddress,
  SetAsUserProfileMailingAddress,
  UserProfile_userProfile as UserProfile,
} from "types/gql-types";
import { deleteUserProfileAddressMutation, setAsUserProfileCurrentAddressMutation, setAsUserProfileMailingAddressMutation } from "pages/dashboard/clients/settings/queries";
import { useSnackbar } from "components/haven-design/snackbar";
import { ProfileAddressesEditModal } from "./add-edit";

type AddressItemActionsType = {
  userProfile:UserProfile | null;
  address:Address;
  disabled?:boolean;
};

type AddressItemActionsMenuType = {
  editAddress: () => void;
  deleteAddress: () => void;
  setCurrentAddress: () => void;
  setMailingAddress: () => void;
} & AddressItemActionsType;

const AddressItemActionsMenu: React.FC<AddressItemActionsMenuType> =
({
  editAddress,
  deleteAddress,
  address,
  userProfile,
  setMailingAddress,
  setCurrentAddress,
}) => {
  const popupState = usePopupState({ variant: "popover", popupId: `address-menu-${address.id}` });
  const isMailingAddress = address.id === userProfile?.mailingAddress?.id;
  const isCurrentAddress = address.id === userProfile?.currentAddress?.id;

  const deleteMenuItem = <MenuItem
    onClick={() => {
      deleteAddress();
      popupState.close();
    }}
    className="color-hc-error"
    disabled={isMailingAddress || isCurrentAddress}
  >
    <Delete />&nbsp; Delete
  </MenuItem>;

  return <>
    <MenuItem onClick={() => {
      editAddress();
      popupState.close();
    }}
    >
      <Edit />&nbsp; Edit Address
    </MenuItem>
    { !isMailingAddress &&
  <MenuItem onClick={() => {
    setMailingAddress();
    popupState.close();
  }}>
    <MailOutline />&nbsp; Set as Mailing Address
  </MenuItem>}
    { !isCurrentAddress &&
  <MenuItem onClick={() => {
    setCurrentAddress();
    popupState.close();
  }}>
    <Home />&nbsp; Set as Current Address
  </MenuItem>}
    {isMailingAddress || isCurrentAddress ? (
      <Tooltip title="Cannot delete current or mailing address.">
        <span>{deleteMenuItem}</span>
      </Tooltip>
    ) : (
      deleteMenuItem
    )}
  </>;
};

const AddressItemActions: React.FC<AddressItemActionsType> = (props) => {
  const { address, disabled } = props;
  const [isMobile, setIsMobile] = React.useState(window.outerWidth <= 695);
  const [isOpen, setIsOpen] = React.useState(false);
  const popupState = usePopupState({ variant: "popover", popupId: `address-menu-${address.id}` });

  const { openSnackbar } = useSnackbar();

  const [setAsMailingMutation, { loading: setAsMailingLoading }] = useMutation<SetAsUserProfileMailingAddress>(setAsUserProfileMailingAddressMutation, { refetchQueries: ["UserProfile"] });
  const [setAsCurrentMutation, { loading: setAsCurrentLoading }] = useMutation<SetAsUserProfileCurrentAddress>(setAsUserProfileCurrentAddressMutation, { refetchQueries: ["UserProfile"] });
  const [deleteAddressMutation, { loading: deleteAddressLoading }] = useMutation<DeleteUserProfileAddress>(deleteUserProfileAddressMutation, { refetchQueries: ["UserProfile"] });

  const size = useWindowSize();
  const disableMenu = disabled ||
    setAsMailingLoading ||
    setAsCurrentLoading ||
    deleteAddressLoading;

  const actionsMenu = () => (
    <AddressItemActionsMenu
      editAddress={() => setIsOpen(true)}
      deleteAddress={
        () => deleteAddressMutation({ variables: { id: address.id } }).then(() => openSnackbar(
          { message: "Address successfully removed.", status: "success" },
        ))
      }
      setMailingAddress={
        () => setAsMailingMutation({ variables: { id: address.id } }).then(() => openSnackbar(
          { message: "Mailing Address successfully updated.", status: "success" },
        ))
      }
      setCurrentAddress={
        () => setAsCurrentMutation({ variables: { id: address.id } }).then(() => openSnackbar(
          { message: "Current Address successfully updated.", status: "success" },
        ))
      }
      {...props} />
  );

  React.useEffect(() => setIsMobile(size.width < 960), [size]);

  return isMobile ? (
    <>
      <ProfileAddressesEditModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        address={address} />
      <IconButton color="primary" size="small"
        disabled={disableMenu}
        {...bindTrigger(popupState)}
      >
        <MoreVert />
      </IconButton>
      <Menu {...bindMenu(popupState)}>
        {actionsMenu()}
      </Menu>
    </>
  ) : (
    <>
      <ProfileAddressesEditModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        address={address} />
      <IconButton color="primary" size="small"
        {...bindHover(popupState)}
        disabled={disableMenu}
      >
        <MoreVert />
      </IconButton>
      <HoverMenu {...bindMenu(popupState)}>
        {actionsMenu()}
      </HoverMenu>
    </>
  );
};

export default AddressItemActions;
